import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { ChartPalette, getChartPalette } from '../../theme/chartPalette';
import { DEFAULT_THEME_MODE, ThemeMode, guidePalettes } from '../../theme/guideTokens';

/**
 * Colores de los gráficos de proyecciones / estadísticas según el tema activo de la guía.
 * Suma a la paleta de gráficos los colores de estado (éxito / error / primario) que los
 * gráficos usan con significado ("en riesgo" va en rojo, "cobrado" en verde).
 */
export interface ChartColors extends ChartPalette {
    success: string;
    danger: string;
    primary: string;
}

interface ThemeSlice {
    Theme?: { mode?: ThemeMode };
}

export const getChartColors = (mode: ThemeMode = DEFAULT_THEME_MODE): ChartColors => {
    const guide = guidePalettes[mode] ?? guidePalettes[DEFAULT_THEME_MODE];
    return {
        ...getChartPalette(mode),
        success: guide.success,
        danger: guide.error,
        primary: guide.accent
    };
};

/**
 * Tema de echarts armado con la paleta: ejes, etiquetas, grilla, leyenda y tooltip toman los
 * colores del tema activo y el fondo queda transparente (se ve la superficie de la tarjeta).
 * Se pasa como `theme` a `ReactECharts`; cuando cambia el modo, el gráfico se vuelve a crear.
 */
export const buildEchartsTheme = (c: ChartPalette) => {
    const axis = {
        axisLine: { lineStyle: { color: c.axisLine } },
        axisTick: { lineStyle: { color: c.axisLine } },
        axisLabel: { color: c.textSecondary },
        splitLine: { lineStyle: { color: [c.gridLine] } },
        splitArea: { areaStyle: { color: ['transparent'] } },
        nameTextStyle: { color: c.textSecondary }
    };
    return {
        color: c.series,
        backgroundColor: 'transparent',
        textStyle: { color: c.text },
        title: { textStyle: { color: c.text }, subtextStyle: { color: c.textSecondary } },
        legend: {
            textStyle: { color: c.textSecondary },
            pageTextStyle: { color: c.textSecondary },
            pageIconColor: c.text,
            pageIconInactiveColor: c.axisLine
        },
        tooltip: {
            backgroundColor: c.surface,
            borderColor: c.axisLine,
            textStyle: { color: c.text },
            axisPointer: {
                lineStyle: { color: c.axisLine },
                crossStyle: { color: c.axisLine },
                shadowStyle: { color: c.gridLine, opacity: 0.35 }
            }
        },
        categoryAxis: axis,
        valueAxis: axis,
        timeAxis: axis,
        logAxis: axis,
        dataZoom: {
            borderColor: c.axisLine,
            textStyle: { color: c.textSecondary },
            handleStyle: { color: c.series[0], borderColor: c.series[0] }
        },
        toolbox: { iconStyle: { borderColor: c.textSecondary } }
    };
};

const useThemeMode = (): ThemeMode => useSelector((state: ThemeSlice) => state.Theme?.mode ?? DEFAULT_THEME_MODE);

/** Colores de gráficos del tema activo (paleta + estados). */
export const useChartColors = (): ChartColors => {
    const mode = useThemeMode();
    return useMemo(() => getChartColors(mode), [mode]);
};

/** Tema de echarts del modo activo (memorizado por modo). */
export const useEchartsTheme = () => {
    const mode = useThemeMode();
    return useMemo(() => buildEchartsTheme(getChartColors(mode)), [mode]);
};

/** Tooltip común (superficie del tema, borde y sombra de la guía). */
export const chartTooltip = (c: ChartPalette) => ({
    backgroundColor: c.surface,
    borderColor: c.axisLine,
    borderWidth: 1,
    textStyle: { color: c.text, fontSize: 12 },
    extraCssText: 'box-shadow: var(--shadow16); border-radius: 6px;'
});
